import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { CRM_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

const keyFeatures = [
    {
        title: "Unified Inbox Across 7 Channels",
        description: "Web chat, voice calls, email, SMS, WhatsApp, Instagram and Facebook all land in the same ticket queue — no tab-hopping between five tools.",
    },
    {
        title: "Contact Identity Resolution",
        description: "Email, phone, WhatsApp number, Instagram handle, and Shopify customer ID all collapse to one contact. One human, one timeline.",
    },
    {
        title: "AI-to-Human Handover",
        description: "The AI agent resolves tier-one, then opens a ticket with the full transcript when it escalates. Your team picks up mid-thread without re-asking.",
    },
    {
        title: "Status, Priority & SLA Tracking",
        description: "Unassigned → Assigned → Open → Pending → Resolved → Closed, with low/medium/high/urgent priorities and SLA timers per ticket.",
    },
    {
        title: "Smart Auto-Assignment",
        description: "Round-robin, channel-specific, or skill-based routing. Supervisors can claim, reassign, or bulk-act from the queue.",
    },
    {
        title: "Saved Views & Filters",
        description: "Slice the inbox by channel, priority, status, tag, or time window — save the view, share it with your team, open in one click.",
    },
    {
        title: "AI Drafts & Summaries",
        description: "Loop the AI back into a human-handled ticket to summarize the thread, draft a reply in the customer's language, or suggest the next step.",
    },
    {
        title: "Role-Scoped Analytics",
        description: "Self, Agent, and Supervisor dashboards — resolution time, CSAT, ticket load, SLA compliance, channel mix, and AI deflection rate.",
    },
    {
        title: "Shopify-Aware Tickets",
        description: "Order, customer, and fulfillment context is attached to every ticket from a Shopify channel — refunds and tracking lookups stay in-thread.",
    },
];

const channels = [
    { name: "Web Chat", icon: "💬" },
    { name: "Voice Calls", icon: "📞" },
    { name: "Email", icon: "📧" },
    { name: "SMS", icon: "📱" },
    { name: "WhatsApp", icon: "🟢" },
    { name: "Instagram", icon: "📷" },
    { name: "Facebook", icon: "👍" },
    { name: "Manual", icon: "✍️" },
];

const workflow = [
    {
        step: "1",
        title: "AI handles the first turn",
        description: "Chat, call, or DM lands. The AI agent answers from your knowledge base, looks up live data, and resolves the easy ones immediately.",
    },
    {
        step: "2",
        title: "Ticket auto-created on escalation",
        description: "If the AI can't resolve or the customer asks for a human, a ticket opens with the full transcript, contact identity, and channel context.",
    },
    {
        step: "3",
        title: "Routed to the right human",
        description: "Auto-assign rules pick the queue, the agent, or escalate to a supervisor. Priority and SLA timers start on the spot.",
    },
    {
        step: "4",
        title: "Human + AI close the loop",
        description: "Your team replies inside the same thread. The AI drafts, summarises, and translates on demand — the human stays in control.",
    },
];

const primaryKeywords = [
    "AI CRM",
    "AI customer support CRM",
    "Unified inbox CRM",
    "Omnichannel ticketing system",
    "AI helpdesk",
    "Conversational CRM",
    "AI chat and call CRM",
    "Customer support inbox software",
    "AI ticket management",
];

export default function CRMMarketingPage() {
    usePageSeo({
        title: "AI CRM — Unified Inbox for Chat, Voice, Email & Social | FloGPT",
        description:
            "AI CRM with a unified inbox for chat, voice, email, SMS, WhatsApp, Instagram, and Facebook. Tickets, human handover, and SLA tracking.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-crm",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI CRM",
                description:
                    "Unified AI-first CRM that turns every chat, call, email, SMS, WhatsApp, Instagram and Facebook conversation into a routed, prioritized ticket — with identity resolution, AI-to-human handover, SLA tracking, and role-scoped analytics.",
                path: "/ai-crm",
                serviceType: "AI CRM / Omnichannel Customer Support Ticketing",
                features: [
                    "Unified inbox across web chat, voice, email, SMS, WhatsApp, Instagram, Facebook",
                    "Contact identity resolution across channels",
                    "AI-to-human ticket handover with full transcript",
                    "Status, priority and SLA tracking per ticket",
                    "Auto-assignment rules and supervisor reassignment",
                    "Saved views, bulk actions and tag-based filters",
                    "AI-drafted replies, summaries and translations",
                    "Self, Agent and Supervisor analytics dashboards",
                    "Shopify order, customer, and fulfillment context on every ticket",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-cyan-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-blue-500/15 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" />
                                    CRM · Unified Inbox · AI-First
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    One Inbox.{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #06b6d4, #3b82f6, #10b981, #06b6d4)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Every Channel.
                                    </motion.span>{" "}
                                    AI on the Front Line.
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    FloGPT's AI CRM turns every chat, call, email, SMS, WhatsApp, Instagram and Facebook conversation into a routed, prioritized ticket — with identity resolution and AI-to-human handover built in.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Start Building Free
                                    </Link>
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a Demo
                                    </a>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· 7 channels unified</span>
                                    <span>· Identity resolved automatically</span>
                                    <span>· AI deflects tier-one tickets</span>
                                </div>
                            </motion.div>

                            {/* CRM inbox preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-cyan-500/25 to-blue-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-white font-semibold">Support Inbox</div>
                                            <div className="text-xs text-slate-400">12 open · 3 unassigned · SLA on track</div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-emerald-300">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                            Live
                                        </div>
                                    </div>
                                    <div className="mt-5 space-y-2">
                                        {[
                                            { ch: "WhatsApp", from: "Priya · +91 98...", subj: "Where is my order #1042?", prio: "high", color: "from-green-500 to-emerald-500" },
                                            { ch: "Voice", from: "Inbound · +1 (555) 010-2025", subj: "Booking · Friday 4:15pm", prio: "medium", color: "from-orange-500 to-pink-500" },
                                            { ch: "Email", from: "alex@acme.com", subj: "Refund request — order #998", prio: "urgent", color: "from-red-500 to-rose-500" },
                                            { ch: "Chat", from: "Anonymous · India", subj: "Does this ship to Mumbai?", prio: "low", color: "from-cyan-500 to-blue-500" },
                                            { ch: "Instagram", from: "@nidhi.styles", subj: "Sizing question on the linen kurta", prio: "medium", color: "from-pink-500 to-fuchsia-500" },
                                        ].map((t, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 8 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.05 * i }}
                                                viewport={{ once: true }}
                                                className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/70 p-3"
                                            >
                                                <span className={`rounded-md bg-gradient-to-r ${t.color} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow`}>
                                                    {t.ch}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[12px] text-slate-400 truncate">{t.from}</div>
                                                    <div className="text-[13px] text-white truncate font-medium">{t.subj}</div>
                                                </div>
                                                <span
                                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                                        t.prio === "urgent"
                                                            ? "bg-red-500/15 text-red-300"
                                                            : t.prio === "high"
                                                            ? "bg-orange-500/15 text-orange-300"
                                                            : t.prio === "medium"
                                                            ? "bg-yellow-500/15 text-yellow-300"
                                                            : "bg-slate-500/15 text-slate-300"
                                                    }`}
                                                >
                                                    {t.prio}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>Auto-assigned to Aria · AI · 0.2s</span>
                                        <span>Handover ready</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">
                                    Your AI is already talking to customers. Now give it a CRM.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                The AI agent handles thousands of conversations a day. Without a CRM purpose-built around it, escalations vanish, identity gets fragmented, and your team works blind.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                FloGPT CRM is the human handover surface for the AI — every escalation arrives with the full transcript, identity resolved, and routed to the right agent before they even hit refresh.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">
                                    Stop paying for three tools that don't talk
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Today you probably run a chatbot, a helpdesk, and a phone system separately — then pay an integration team to glue them.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT collapses the stack:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "One inbox for every channel — no more tab juggling",
                                    "One contact record per customer — no more duplicate identities",
                                    "One AI brain across chat and voice — no more retraining a second bot",
                                    "One analytics surface — no more reconciling three dashboards",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-cyan-400 via-blue-400 to-emerald-400 shadow-[0_0_0_3px_rgba(6,182,212,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">One platform. One bill. One CRM that actually knows your customer.</p>
                        </article>
                    </motion.div>
                </section>

                {/* CHANNELS */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Every channel, one queue</h2>
                        <p className="mt-4 text-slate-300">
                            Wherever the customer reaches out, the ticket lands in the same place — with channel-specific defaults already configured.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
                        {channels.map((c) => (
                            <motion.div
                                key={c.name}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                viewport={{ once: true }}
                                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-center shadow"
                            >
                                <div className="text-3xl">{c.icon}</div>
                                <div className="mt-2 text-sm font-semibold text-slate-200">{c.name}</div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* WORKFLOW */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">How a conversation becomes a closed ticket</h2>
                        <p className="mt-4 text-slate-300">
                            The AI is the front line. The CRM is where humans take over — without retyping a thing.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {workflow.map((s, idx) => (
                            <motion.article
                                key={s.step}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="relative rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20"
                            >
                                <div className="absolute -top-3 left-6 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-[12px] font-bold text-white shadow">
                                    {s.step}
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                                <p className="mt-2 text-slate-300 leading-relaxed text-sm">{s.description}</p>
                            </motion.article>
                        ))}
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the FloGPT CRM does</h2>
                        <p className="mt-4 text-slate-300">
                            Designed from day one for AI-first support teams — not bolted onto a legacy ticketing tool.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/15 border border-cyan-400/30">
                                        <svg className="h-5 w-5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h2 className="text-lg font-semibold text-white">Pair the CRM with</h2>
                        <p className="mt-2 text-slate-400 text-sm">The CRM shines when every FloGPT channel feeds into it.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-3">
                            <Link to="/ai-chat-widget" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-cyan-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Chat Widget →</div>
                                <p className="mt-1 text-sm text-slate-400">Embed the chat widget; every escalation lands as a CRM ticket.</p>
                            </Link>
                            <Link to="/ai-voice-agents" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-cyan-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Voice Agents →</div>
                                <p className="mt-1 text-sm text-slate-400">Inbound and outbound calls auto-create tickets with full transcripts.</p>
                            </Link>
                            <Link to="/ai-knowledge-base" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-cyan-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Knowledge Base →</div>
                                <p className="mt-1 text-sm text-slate-400">The AI deflects tier-one tickets by answering from your real docs.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                <FAQSection
                    id="crm"
                    eyebrow="CRM · FAQ"
                    heading="Common questions about the AI CRM"
                    items={CRM_FAQS}
                />

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See the AI CRM running on your channels</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll wire up a live inbox across the channels you actually use.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free CRM Demo"
                            subtitle="Tell us your channel mix — we'll show a live unified inbox running on your stack"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>
            </main>
            <Footer />

            <JsonLd id="service-crm" data={serviceSchema} />
        </div>
    );
}
